import React, { FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Button from 'components/Button';
import { SelectInput, TextInput } from 'components/Forms/lib';
import { OptionsProps } from 'components/Forms/lib/SelectInput';
import { logEvent } from 'config/tracking';
import { REGEXP } from 'constants/validationsForm';
import { Civility } from 'core/enums/civility';
import { enumKeys } from 'utils/common';
import { TrackingEventName } from 'utils/types/amplitude';
import { SFormError, SIntro, SNotice } from './styles';
import useSignUpRequest, { SignUpInputs } from './useSignUpRequest';

type SignUpFormProps = {
    onSignedUp(inputs: SignUpInputs): void;
};

const SignUpForm: FC<SignUpFormProps> = ({ onSignedUp }) => {
    const { t } = useTranslation();
    const methods = useForm<SignUpInputs>();
    const { control, handleSubmit, setError, formState } = methods;
    const { captcha, failure, send } = useSignUpRequest();

    const civilityOptions: OptionsProps = useMemo(
        () =>
            enumKeys(Civility).map((key) => ({
                label: t(`common.civility.enum.${Civility[key]}`),
                value: Civility[key],
            })),
        [t],
    );

    const onSubmit = handleSubmit(async (values: SignUpInputs) => {
        const inputs = { ...values, email: values.email.trim() };
        const signUpFailure = await send(inputs);

        if (!signUpFailure) {
            logEvent({ name: TrackingEventName.SIGN_UP_REQUESTED, params: {} });
            onSignedUp(inputs);
        } else if ('invalid-email' === signUpFailure) {
            setError('email', { message: t('sign-up-form.errors.invalid-email') }, { shouldFocus: true });
        }
    });

    return (
        <FormProvider {...methods}>
            <SIntro>{t('sign-up-form.intro')}</SIntro>
            <form onSubmit={onSubmit} noValidate>
                <SelectInput
                    control={control}
                    label={t('common.civility.label')}
                    name="civility"
                    options={civilityOptions}
                    placeholder={t('sign-up-form.civility.placeholder')}
                />
                <TextInput
                    label={t('common.first-name.label')}
                    name="firstName"
                    placeholder={t('sign-up-form.first-name.placeholder')}
                    required
                />
                <TextInput
                    label={t('common.last-name.label')}
                    name="lastName"
                    placeholder={t('sign-up-form.last-name.placeholder')}
                    required
                />
                <TextInput
                    label={t('common.email.label')}
                    name="email"
                    pattern={{ message: t(REGEXP.EMAIL.message), value: REGEXP.EMAIL.value }}
                    placeholder={t('sign-up-form.email.placeholder')}
                    transform={(value) => value.trimStart()}
                    type="email"
                    required
                />
                <TextInput
                    label={t('sign-up-form.origin.label')}
                    name="origin"
                    placeholder={t('sign-up-form.origin.placeholder')}
                />
                {captcha}
                {failure && <SFormError role="alert">{t(`sign-up-form.errors.${failure}`)}</SFormError>}
                <Button disabled={formState.isSubmitting} large submit>
                    {t('sign-up-form.submit')}
                </Button>
            </form>
            <SNotice>
                {t('sign-up-form.notice')}{' '}
                <Link rel="noopener noreferrer" target="_blank" to="/legal-notice">
                    {t('sign-up-form.notice-link')}
                </Link>
                .
            </SNotice>
        </FormProvider>
    );
};

export default SignUpForm;
